import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { config } from '@/config';
import {
  type MetricThresholdEnabledState,
  MetricThresholdsSection,
} from '@/features/projects/components/MetricThresholdsSection';
import { ProjectBadgeField } from '@/features/projects/components/ProjectBadgeField';
import {
  PipelineWebhookDescriptionSection,
  PipelineWebhookKeySection,
  PipelineWebhookUrlSection,
} from '@/features/projects/components/ProjectWebhookFields';
import { ProjectWebhookSnippetSection } from '@/features/projects/components/ProjectWebhookSnippetSection';
import {
  parseAvgDurationSeconds,
  parseFrequencyPerDay,
  parseGreenTimeDays,
  parseMttfHours,
  parseThresholdValue,
} from '@/features/projects/metricThresholdDrafts';
import {
  isMetricThresholdDisabled,
  metricThresholdsExecutionRuns,
  metricThresholdsJobs,
  metricThresholdsPipelines,
  metricThresholdsStages,
  metricThresholdsSuites,
  metricThresholdsTests,
} from '@/shared/utils/metricsThresholds';
import { componentSizes } from '@/theme/componentSizes';
import { s, type SpacingToken } from '@/theme/tokens';
import { Box, formatDate, Panel, Stack, StatusBanner, Text } from '@/ui';
import {
  settingsCopyTextSx,
  settingsPanelSurfaceSx,
  settingsValueTextSx,
} from '@/ui/composites/settingsPageTypographySx';
import { Button } from '@/ui/primitives/Button';
import { TextField } from '@/ui/primitives/TextField';
import { buildWebhookSnippet, parseWebhookTagValues } from './projectWebhookHelpers';
import type {
  useRotateProjectApiKey,
  useTestProjectPipelineWebhook,
  useUpdateProject,
} from '@/features/projects';
import type { useMetricThresholdDrafts } from '@/features/projects/hooks/useMetricThresholdDrafts';
import type { MetricThresholdDrafts } from '@/features/projects/metricThresholdDrafts';
import type { MetricThresholdGroup, ProjectDto } from '@flowqa/shared';

const defaultThresholdGroupByKey: Record<
  ProjectMetricThresholdSectionProps['thresholdKey'],
  MetricThresholdGroup
> = {
  suites: metricThresholdsSuites,
  executionRuns: metricThresholdsExecutionRuns,
  tests: metricThresholdsTests,
  pipelines: metricThresholdsPipelines,
  stages: metricThresholdsStages,
  jobs: metricThresholdsJobs,
};

const mergeThresholdGroup = (
  base: MetricThresholdGroup,
  update?: Partial<MetricThresholdGroup>,
): MetricThresholdGroup => ({
  passRate: { ...base.passRate, ...(update?.passRate ?? {}) },
  frequency: { ...base.frequency, ...(update?.frequency ?? {}) },
  avgDuration: { ...base.avgDuration, ...(update?.avgDuration ?? {}) },
  flakeScore: { ...base.flakeScore, ...(update?.flakeScore ?? {}) },
  mttf: { ...base.mttf, ...(update?.mttf ?? {}) },
  greenTime: { ...base.greenTime, ...(update?.greenTime ?? {}) },
});

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };

type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

type ProjectDetailsSectionProps = {
  project: ProjectDto | null;
  nameValue: string;
  setNameValue: (value: string) => void;
  nameDirty: boolean;
  setNameDirty: (value: boolean) => void;
  badgeCharsValue: string;
  setBadgeCharsValue: (value: string) => void;
  badgeColorValue: ProjectDto['badgeColor'];
  setBadgeColorValue: (value: ProjectDto['badgeColor']) => void;
  updateProject: ReturnType<typeof useUpdateProject>;
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
};

type ProjectApiKeySectionProps = {
  project: ProjectDto | null;
  apiKeyValue: string;
  setApiKeyValue: (value: string) => void;
  revokeOpen: boolean;
  setRevokeOpen: (open: boolean) => void;
  updateProject: ReturnType<typeof useUpdateProject>;
  rotateProjectApiKey: ReturnType<typeof useRotateProjectApiKey>;
  testProjectPipelineWebhook: ReturnType<typeof useTestProjectPipelineWebhook>;
  apiKeyError: MutationErrorController;
  notify: (input: NotifyInput) => void;
  container?: 'panel' | 'none';
};

type ProjectMetricThresholdSectionProps = {
  title: string;
  description: string;
  successMessage: string;
  thresholdKey: 'suites' | 'executionRuns' | 'tests' | 'pipelines' | 'stages' | 'jobs';
  drafts: MetricThresholdDrafts;
  setDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  preferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  isDirty: boolean;
  isValid: boolean;
  isPending: boolean;
  gridSpacing: SpacingToken;
  project: ProjectDto | null;
  updateProject: ReturnType<typeof useUpdateProject>;
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
  resetDrafts: (thresholds: MetricThresholdGroup) => void;
  container?: 'panel' | 'none';
};

const buildMetricThresholdPayload = (
  drafts: MetricThresholdDrafts,
  enabled: MetricThresholdEnabledState,
  current: MetricThresholdGroup,
): MetricThresholdGroup => ({
  passRate: {
    target: parseThresholdValue(drafts.passRate.target) ?? current.passRate.target,
    warn: parseThresholdValue(drafts.passRate.warn) ?? current.passRate.warn,
    direction: 'higherBetter',
    enabled: enabled.passRate,
  },
  frequency: {
    target:
      parseFrequencyPerDay(drafts.frequency.targetValue, drafts.frequency.targetUnit) ??
      current.frequency.target,
    warn:
      parseFrequencyPerDay(drafts.frequency.warnValue, drafts.frequency.warnUnit) ??
      current.frequency.warn,
    direction: 'higherBetter',
    enabled: enabled.frequency,
    targetUnit: drafts.frequency.targetUnit,
    warnUnit: drafts.frequency.warnUnit,
  },
  avgDuration: {
    target:
      parseAvgDurationSeconds(drafts.avgDuration.targetValue, drafts.avgDuration.targetUnit) ??
      current.avgDuration.target,
    warn:
      parseAvgDurationSeconds(drafts.avgDuration.warnValue, drafts.avgDuration.warnUnit) ??
      current.avgDuration.warn,
    direction: 'lowerBetter',
    enabled: enabled.avgDuration,
    targetUnit: drafts.avgDuration.targetUnit,
    warnUnit: drafts.avgDuration.warnUnit,
  },
  flakeScore: {
    target: parseThresholdValue(drafts.flakeScore.target) ?? current.flakeScore.target,
    warn: parseThresholdValue(drafts.flakeScore.warn) ?? current.flakeScore.warn,
    direction: 'lowerBetter',
    enabled: current.flakeScore.enabled,
  },
  mttf: {
    target: parseMttfHours(drafts.mttf.targetValue, drafts.mttf.targetUnit) ?? current.mttf.target,
    warn: parseMttfHours(drafts.mttf.warnValue, drafts.mttf.warnUnit) ?? current.mttf.warn,
    direction: 'higherBetter',
    enabled: current.mttf.enabled,
  },
  greenTime: {
    target:
      parseGreenTimeDays(drafts.greenTime.targetValue, drafts.greenTime.targetUnit) ??
      current.greenTime.target,
    warn:
      parseGreenTimeDays(drafts.greenTime.warnValue, drafts.greenTime.warnUnit) ??
      current.greenTime.warn,
    direction: 'higherBetter',
    enabled: current.greenTime.enabled,
  },
});

const renderPanel = (
  title: string,
  children: ReactElement,
  actions?: ReactElement,
  titleSlot?: ReactElement,
): ReactElement => (
  <Panel
    title={title}
    border={false}
    actions={actions}
    titleSlot={titleSlot}
    sx={settingsPanelSurfaceSx}
  >
    {children}
  </Panel>
);

export const ProjectDetailsSection = ({
  project,
  nameValue,
  setNameValue,
  nameDirty,
  setNameDirty,
  badgeCharsValue,
  setBadgeCharsValue,
  badgeColorValue,
  setBadgeColorValue,
  updateProject,
  mutationError,
  notify,
}: ProjectDetailsSectionProps): ReactElement => {
  const syncDetailsDirty = (
    nextName: string,
    nextBadgeChars: string,
    nextBadgeColor: ProjectDto['badgeColor'],
  ): void => {
    if (!project) return;
    setNameDirty(
      nextName.trim() !== project.name.trim() ||
        nextBadgeChars !== project.badgeChars ||
        nextBadgeColor !== project.badgeColor,
    );
  };

  return renderPanel(
    'Project Details',
    <Stack gap="md">
      <Stack gap="xxs">
        <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
          Project ID
        </Text>
        <Text role="body" sx={settingsValueTextSx}>
          {project?.id ?? ''}
        </Text>
      </Stack>
      <TextField
        label="Project Name"
        fieldStyle="settings"
        value={nameValue}
        onChange={(event) => {
          const nextValue = event.target.value;
          setNameValue(nextValue);
          syncDetailsDirty(nextValue, badgeCharsValue, badgeColorValue);
        }}
        disabled={updateProject.isPending}
      />
      <Box sx={{ mt: s('sm') }}>
        <ProjectBadgeField
          chars={badgeCharsValue}
          color={badgeColorValue}
          disabled={updateProject.isPending}
          onCharsChange={(nextValue) => {
            setBadgeCharsValue(nextValue);
            syncDetailsDirty(nameValue, nextValue, badgeColorValue);
          }}
          onColorChange={(nextValue) => {
            setBadgeColorValue(nextValue);
            syncDetailsDirty(nameValue, badgeCharsValue, nextValue);
          }}
        />
      </Box>
    </Stack>,
    <>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disabled={!nameDirty || updateProject.isPending}
        onClick={() => {
          if (!project) return;
          setNameValue(project.name);
          setBadgeCharsValue(project.badgeChars);
          setBadgeColorValue(project.badgeColor);
          setNameDirty(false);
          mutationError.clearError();
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        fullWidthOnMobile={false}
        disabled={!nameDirty || updateProject.isPending || badgeCharsValue.length === 0}
        onClick={() => {
          if (!project) return;
          updateProject.mutate(
            {
              id: project.id,
              name: nameValue,
              badgeChars: badgeCharsValue,
              badgeColor: badgeColorValue,
            },
            {
              onSuccess: (updated) => {
                setNameValue(updated.name);
                setBadgeCharsValue(updated.badgeChars);
                setBadgeColorValue(updated.badgeColor);
                setNameDirty(false);
                mutationError.clearError();
                notify({ message: `Updated project ${updated.name}`, tone: 'green' });
              },
              onError: (err) => {
                mutationError.setError(err);
              },
            },
          );
        }}
      >
        Update
      </Button>
    </>,
  );
};

export const ProjectApiKeySection = ({
  project,
  apiKeyValue,
  setApiKeyValue,
  revokeOpen,
  setRevokeOpen,
  updateProject,
  rotateProjectApiKey,
  testProjectPipelineWebhook,
  apiKeyError,
  notify,
  container = 'panel',
}: ProjectApiKeySectionProps): ReactElement => {
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [generatedApiKeyCreatedAt, setGeneratedApiKeyCreatedAt] = useState<Date | null>(null);
  const [generatedApiKeyLastUsedAt, setGeneratedApiKeyLastUsedAt] = useState<Date | null>(null);
  const [webhookTagsInput, setWebhookTagsInput] = useState('');
  const actionIconSize = componentSizes.auth.inputHeight - 2;
  const hasPlaintextApiKey = apiKeyValue.trim().length > 0;
  const hasConfiguredApiKey = hasPlaintextApiKey || Boolean(project?.hasProjectApiKey);
  const createdAt = generatedApiKeyCreatedAt ?? project?.projectApiKeyCreatedAt ?? null;
  const lastUsedAt = generatedApiKeyLastUsedAt ?? project?.projectApiKeyLastUsedAt ?? null;
  const createdLabel = formatDate(createdAt, { includeTime: true }) || '-';
  const lastUsedLabel = formatDate(lastUsedAt, { includeTime: true }) || '-';
  const displayApiKeyValue = hasPlaintextApiKey
    ? apiKeyValue
    : hasConfiguredApiKey
      ? '••••••••••••••••'
      : '';
  const apiBaseUrl = config.apiBaseUrl.replace(/\/+$/, '');
  const webhookEndpoint = project
    ? `${apiBaseUrl}/webhooks/pipelines/${project.id}`
    : `${apiBaseUrl}/webhooks/pipelines/{projectId}`;
  const webhookTagValues = useMemo(
    () => parseWebhookTagValues(webhookTagsInput),
    [webhookTagsInput],
  );
  const webhookSnippet = useMemo(
    () => buildWebhookSnippet(webhookEndpoint, webhookTagValues),
    [webhookEndpoint, webhookTagValues],
  );
  const actionGroupSx = {
    backgroundColor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    overflow: 'hidden',
    '& .MuiButton-root': {
      color: 'text.primary',
      borderColor: 'divider',
      minWidth: 0,
    },
    '& .MuiButtonGroup-grouped': {
      width: actionIconSize,
      minWidth: actionIconSize,
      maxWidth: actionIconSize,
      flex: `0 0 ${actionIconSize}px`,
      padding: 0,
    },
    '& .MuiButtonGroup-grouped:not(:last-of-type)': {
      borderRight: '1px solid',
      borderColor: 'divider',
    },
    '& .MuiButton-root.Mui-disabled': {
      color: 'text.disabled',
    },
  } as const;
  const actionButtonSx = {
    width: actionIconSize,
    height: actionIconSize,
    minWidth: actionIconSize,
    minHeight: actionIconSize,
    padding: 0,
    borderRadius: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    '& .MuiSvgIcon-root': {
      fontSize: 17,
      display: 'block',
    },
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  } as const;
  const onCopyWebhookUrl = (): void => {
    void navigator.clipboard.writeText(webhookEndpoint);
    notify({ message: 'Copied webhook URL', tone: 'green' });
  };
  const onCopyWebhookKey = (): void => {
    if (!hasPlaintextApiKey) return;
    void navigator.clipboard.writeText(apiKeyValue);
    notify({ message: 'API key copied to clipboard', tone: 'green' });
  };
  const onCopyWebhookSnippet = (): void => {
    void navigator.clipboard.writeText(webhookSnippet);
    notify({ message: 'Copied webhook snippet', tone: 'green' });
  };
  useEffect(() => {
    if (!project) return;
    setWebhookTagsInput((project.projectApiKeyTags ?? []).join(', '));
  }, [project?.id, project?.projectApiKeyTags]);
  const applyGeneratedWebhookKey = (): void => {
    if (!project) return;
    rotateProjectApiKey.mutate(
      { id: project.id, tags: webhookTagValues },
      {
        onSuccess: (result) => {
          setApiKeyValue(result.apiKey);
          setGeneratedApiKeyCreatedAt(new Date(result.createdAt));
          setGeneratedApiKeyLastUsedAt(null);
          setWebhookTagsInput((result.tags ?? []).join(', '));
          setRegenerateOpen(false);
          apiKeyError.clearError();
          notify({
            message: hasConfiguredApiKey
              ? 'Regenerated project API key'
              : 'Generated project API key',
            tone: 'green',
          });
        },
        onError: (err) => {
          apiKeyError.setError(err);
        },
      },
    );
  };
  const onGenerateOrRegenerateWebhookKey = (): void => {
    if (!project) return;
    if (hasConfiguredApiKey) {
      setRegenerateOpen(true);
      return;
    }
    applyGeneratedWebhookKey();
  };
  const onRevokeWebhookKey = (): void => {
    setRevokeOpen(true);
  };
  const onTestWebhookConnection = (): void => {
    if (!(project && hasPlaintextApiKey)) return;
    testProjectPipelineWebhook.mutate(
      { id: project.id, apiKey: apiKeyValue },
      {
        onSuccess: () => {
          setGeneratedApiKeyLastUsedAt(new Date());
          apiKeyError.clearError();
          notify({ message: 'Webhook key is valid and ready to use', tone: 'green' });
        },
        onError: (err) => {
          apiKeyError.setError(err);
        },
      },
    );
  };
  const canTestConnection =
    Boolean(project) &&
    hasPlaintextApiKey &&
    !updateProject.isPending &&
    !rotateProjectApiKey.isPending &&
    !testProjectPipelineWebhook.isPending;
  const content = (
    <Stack gap="md">
      {container === 'panel' ? (
        <Text role="caption" color="textSecondary">
          Use this key to authenticate pipeline webhook events for this project.
        </Text>
      ) : null}
      <Stack gap="lg">
        <Stack gap="md">
          <PipelineWebhookDescriptionSection />
          <PipelineWebhookUrlSection
            webhookEndpoint={webhookEndpoint}
            actionGroupSx={actionGroupSx}
            actionButtonSx={actionButtonSx}
            onCopy={onCopyWebhookUrl}
          />
          <PipelineWebhookKeySection
            value={displayApiKeyValue}
            canCopy={
              hasPlaintextApiKey && !updateProject.isPending && !rotateProjectApiKey.isPending
            }
            canTest={canTestConnection}
            canGenerateOrRegenerate={
              Boolean(project) && !updateProject.isPending && !rotateProjectApiKey.isPending
            }
            canRevoke={
              Boolean(project) &&
              hasConfiguredApiKey &&
              !updateProject.isPending &&
              !rotateProjectApiKey.isPending
            }
            generateLabel={hasConfiguredApiKey ? 'Regenerate Webhook Key' : 'Generate Webhook Key'}
            actionGroupSx={actionGroupSx}
            actionButtonSx={actionButtonSx}
            onCopy={onCopyWebhookKey}
            onTest={onTestWebhookConnection}
            onGenerateOrRegenerate={onGenerateOrRegenerateWebhookKey}
            onRevoke={onRevokeWebhookKey}
          />
          <ProjectWebhookSnippetSection
            tagsInput={webhookTagsInput}
            onTagsInputChange={setWebhookTagsInput}
            snippet={webhookSnippet}
            onCopySnippet={onCopyWebhookSnippet}
            disabled={updateProject.isPending || rotateProjectApiKey.isPending}
          />
          {hasPlaintextApiKey ? (
            <StatusBanner
              tone="blue"
              variant="outlined"
              message="Copy this key now. It is only shown once and will be hidden afterward."
              sx={{ mt: s('xs') }}
            />
          ) : null}
          <Box sx={{ display: 'flex', gap: s('md'), width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <Stack gap="xxs">
                <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                  Created
                </Text>
                <Text role="body" sx={settingsValueTextSx}>
                  {createdLabel}
                </Text>
              </Stack>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Stack gap="xxs">
                <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                  Last Used
                </Text>
                <Text role="body" sx={settingsValueTextSx}>
                  {lastUsedLabel}
                </Text>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <>
      {container === 'panel' ? renderPanel('Pipeline Webhooks', content) : content}
      <ConfirmDialog
        open={regenerateOpen}
        title="Regenerate Webhook Key"
        description={
          <Stack gap="md">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Generate a new webhook key for this project.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              This action revokes the existing webhook key immediately.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Update your pipeline webhook secret token after regenerating to avoid webhook
              failures.
            </Text>
          </Stack>
        }
        confirmLabel="Regenerate Webhook Key"
        confirmColor="primary"
        confirmVariant="contained"
        loading={updateProject.isPending || rotateProjectApiKey.isPending}
        onClose={() => {
          setRegenerateOpen(false);
        }}
        onConfirm={() => {
          applyGeneratedWebhookKey();
        }}
      />
      <ConfirmDialog
        open={revokeOpen}
        title="Revoke Webhook Key"
        description={
          <Stack gap="md">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Delete this webhook key to immediately block pipeline webhook requests using it.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              This action cannot be undone.
            </Text>
          </Stack>
        }
        confirmLabel="Revoke Webhook Key"
        confirmColor="error"
        confirmVariant="contained"
        loading={updateProject.isPending || rotateProjectApiKey.isPending}
        onClose={() => {
          setRevokeOpen(false);
        }}
        onConfirm={() => {
          if (!project) return;
          updateProject.mutate(
            { id: project.id, projectApiKey: null },
            {
              onSuccess: () => {
                setApiKeyValue('');
                setGeneratedApiKeyCreatedAt(null);
                setGeneratedApiKeyLastUsedAt(null);
                apiKeyError.clearError();
                notify({ message: 'Revoked webhook key', tone: 'green' });
                setRevokeOpen(false);
              },
              onError: (err) => {
                apiKeyError.setError(err);
              },
            },
          );
        }}
      />
    </>
  );
};

export const ProjectMetricThresholdSection = ({
  title,
  description,
  successMessage,
  thresholdKey,
  drafts,
  setDrafts,
  preferredUnitsRef,
  isDirty,
  isValid,
  isPending,
  gridSpacing,
  project,
  updateProject,
  mutationError,
  notify,
  resetDrafts,
  container = 'panel',
}: ProjectMetricThresholdSectionProps): ReactElement => {
  const deriveInitialEnabled = useMemo(
    () =>
      (thresholds: MetricThresholdGroup | null | undefined): MetricThresholdEnabledState => ({
        passRate: thresholds ? !isMetricThresholdDisabled(thresholds.passRate) : true,
        frequency: thresholds ? !isMetricThresholdDisabled(thresholds.frequency) : true,
        avgDuration: thresholds ? !isMetricThresholdDisabled(thresholds.avgDuration) : true,
      }),
    [],
  );
  const memoizedThresholds = useMemo(
    () =>
      project
        ? mergeThresholdGroup(
            defaultThresholdGroupByKey[thresholdKey],
            project.metricThresholds[thresholdKey],
          )
        : defaultThresholdGroupByKey[thresholdKey],
    [project, thresholdKey],
  );
  const initialEnabled = useMemo(
    () => deriveInitialEnabled(memoizedThresholds),
    [deriveInitialEnabled, memoizedThresholds],
  );

  return (
    <MetricThresholdsSection
      title={title}
      description={description}
      passRateLabel={thresholdKey === 'pipelines' ? 'Success Rate' : 'Pass Rate'}
      frequencyLabel={(() => {
        if (thresholdKey === 'pipelines') {
          return 'Job Frequency';
        }
        if (thresholdKey === 'stages') {
          return 'Stage Frequency';
        }
        if (thresholdKey === 'jobs') {
          return 'Job Frequency';
        }
        return 'Execution Frequency';
      })()}
      drafts={drafts}
      setDrafts={setDrafts}
      preferredUnitsRef={preferredUnitsRef}
      initialEnabled={initialEnabled}
      isDirty={isDirty}
      isValid={isValid}
      isPending={isPending}
      gridSpacing={gridSpacing}
      container={container}
      onCancel={() => {
        if (!project) return;
        resetDrafts(memoizedThresholds);
        mutationError.clearError();
      }}
      onSubmit={(enabled) => {
        if (!project) return;
        const payload = buildMetricThresholdPayload(drafts, enabled, memoizedThresholds);
        updateProject.mutate(
          { id: project.id, metricThresholds: { [thresholdKey]: payload } },
          {
            onSuccess: () => {
              resetDrafts(payload);
              mutationError.clearError();
              notify({ message: successMessage, tone: 'green' });
            },
            onError: (err) => {
              mutationError.setError(err);
            },
          },
        );
      }}
    />
  );
};
